import * as React from 'react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export interface TimelineItem {
  id: string;
  title: string;
  description?: string;
  timestamp: string; // ISO or human-readable
  icon?: React.ReactNode;
  type?: 'info' | 'success' | 'warning' | 'error';
  user?: { name: string; avatar?: string };
}

export interface TimelineProps {
  items: TimelineItem[];
  maxItems?: number;
  showMore?: () => void;
  className?: string;
}

// Get color for timeline dot based on type
function getTypeColor(type?: string): string {
  const typeColorMap: Record<string, string> = {
    info: 'bg-[var(--color-primary)]',
    success: 'bg-[var(--color-success)]',
    warning: 'bg-[var(--color-warning)]',
    error: 'bg-[var(--color-destructive)]',
  };

  return typeColorMap[type || 'info'] || 'bg-[var(--color-primary)]';
}

// Generate initials from name
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Get color for avatar
function getAvatarColor(name: string): string {
  const colors = [
    'bg-[var(--color-primary)]',
    'bg-[var(--color-success)]',
    'bg-[var(--color-warning)]',
    'bg-[var(--color-destructive)]',
    'bg-[var(--color-secondary)]',
  ];
  const hash = name.charCodeAt(0) + name.charCodeAt(name.length - 1);
  return colors[hash % colors.length];
}

// Format timestamp
function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  } catch {
    return timestamp;
  }
}

export const Timeline = React.forwardRef<HTMLDivElement, TimelineProps>(
  ({ items, maxItems, showMore, className }, ref) => {
    const displayItems = maxItems ? items.slice(0, maxItems) : items;
    const hasMore = maxItems && items.length > maxItems;

    return (
      <div ref={ref} className={cn('space-y-4', className)}>
        {displayItems.map((item, idx) => {
          const isLast = idx === displayItems.length - 1;
          const dotColor = getTypeColor(item.type);
          const userInitials = item.user ? getInitials(item.user.name) : null;
          const userAvatarColor = item.user ? getAvatarColor(item.user.name) : '';

          return (
            <div key={item.id} className="flex gap-4">
              {/* Timeline connector */}
              <div className="flex flex-col items-center gap-2">
                {/* Dot or icon */}
                <div className={cn('h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0', dotColor)}>
                  {item.icon ? (
                    <div className="h-4 w-4 text-white">{item.icon}</div>
                  ) : (
                    <div className="h-3 w-3 rounded-full bg-white" />
                  )}
                </div>

                {/* Connecting line */}
                {!isLast && (
                  <div className="w-0.5 h-12 bg-[var(--color-border)]" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 pb-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="font-medium text-[var(--color-foreground)]">
                      {item.title}
                    </p>

                    {item.description && (
                      <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                        {item.description}
                      </p>
                    )}

                    {item.user && (
                      <div className="mt-2 flex items-center gap-2">
                        {item.user.avatar ? (
                          <img
                            src={item.user.avatar}
                            alt={item.user.name}
                            className="h-6 w-6 rounded-full object-cover"
                          />
                        ) : (
                          <div
                            className={cn(
                              'h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold text-white',
                              userAvatarColor,
                            )}
                          >
                            {userInitials}
                          </div>
                        )}
                        <span className="text-xs font-medium text-[var(--color-muted-foreground)]">
                          {item.user.name}
                        </span>
                      </div>
                    )}
                  </div>

                  <span className="text-xs font-medium text-[var(--color-muted-foreground)] flex-shrink-0 whitespace-nowrap">
                    {formatTimestamp(item.timestamp)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Show More button */}
        {hasMore && showMore && (
          <div className="flex justify-center pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={showMore}
            >
              Show More
            </Button>
          </div>
        )}

        {/* Empty state */}
        {items.length === 0 && (
          <div className="flex items-center justify-center py-8 text-center">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              No timeline events
            </p>
          </div>
        )}
      </div>
    );
  },
);

Timeline.displayName = 'Timeline';
